import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Brain, Database, Shield, Zap, BookOpen, AlertTriangle, TrendingUp } from "lucide-react";

interface AmIOKScoringExplanationProps {
  aiScore?: number;
  confidence?: number;
  reasoning?: string[];
}

const AmIOKScoringExplanation = ({ aiScore, confidence, reasoning }: AmIOKScoringExplanationProps) => {
  const getScoringBreakdown = () => {
    if (!aiScore) return null;

    const factors = [
      {
        name: "Emergency Screening",
        weight: "Highest Priority",
        description: "Immediate risk indicators override all other factors",
        value: aiScore >= 85 ? "Positive" : "Negative",
        color: aiScore >= 85 ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"
      },
      {
        name: "Daily Impact Score",
        weight: "30 points max",
        description: "Functional impairment assessment (0-10 scale × 3)",
        value: `${Math.min(30, Math.floor(aiScore * 0.3))} points`,
        color: "bg-blue-100 text-blue-800"
      },
      {
        name: "Symptom Analysis",
        weight: "20 points max",
        description: "Pattern recognition for concerning symptoms",
        value: `${Math.min(20, Math.floor(aiScore * 0.2))} points`,
        color: "bg-yellow-100 text-yellow-800"
      },
      {
        name: "Medical Evidence",
        weight: "15 points max",
        description: "Literature-based severity indicators",
        value: `${Math.min(15, Math.floor(aiScore * 0.15))} points`,
        color: "bg-purple-100 text-purple-800"
      },
      {
        name: "WHO ICD-11 Mapping",
        weight: "Variable",
        description: "International diagnostic classification system",
        value: "Integrated",
        color: "bg-indigo-100 text-indigo-800"
      }
    ];

    return factors;
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="flex items-center gap-2">
          <Brain className="w-4 h-4" />
          How We Worked Out Your Score
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Brain className="w-5 h-5" />
            AI-Driven AmIOK Scoring System
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Overview */}
          <Card className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <div className="flex items-start gap-3">
              <Zap className="w-6 h-6 text-blue-600 mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Intelligent Clinical Assessment</h3>
                <p className="text-gray-700 text-sm">
                  Our AI system analyzes your responses using evidence-based medical literature, 
                  WHO ICD-11 diagnostic criteria, and clinical decision support algorithms to generate 
                  a comprehensive risk assessment score (0-100).
                </p>
              </div>
            </div>
          </Card>

          {/* Data Sources */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Database className="w-5 h-5" />
              Medical Data Sources
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <BookOpen className="w-4 h-4 text-green-600" />
                  <span className="font-medium">PubMed Research</span>
                </div>
                <p className="text-sm text-gray-600">
                  Peer-reviewed medical literature and clinical studies
                </p>
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-4 h-4 text-blue-600" />
                  <span className="font-medium">WHO ICD-11</span>
                </div>
                <p className="text-sm text-gray-600">
                  International classification of diseases and health problems
                </p>
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-purple-600" />
                  <span className="font-medium">Clinical Guidelines</span>
                </div>
                <p className="text-sm text-gray-600">
                  Evidence-based practice recommendations
                </p>
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-orange-600" />
                  <span className="font-medium">FDA Safety Data</span>
                </div>
                <p className="text-sm text-gray-600">
                  Drug safety information and adverse event reports
                </p>
              </Card>
            </div>
          </div>

          {/* Scoring Breakdown */}
          {aiScore && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Score Breakdown</h3>
              <div className="space-y-3">
                {getScoringBreakdown()?.map((factor, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{factor.name}</span>
                        <Badge variant="outline" className="text-xs">
                          {factor.weight}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-600">{factor.description}</p>
                    </div>
                    <Badge className={`${factor.color} ml-3`}>
                      {factor.value}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Score Ranges */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Score Interpretation</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-3 p-3 border rounded-lg">
                <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                <span className="font-medium">0-39: Very Low to Minimal Risk</span>
                <span className="text-sm text-gray-600 ml-auto">Continue monitoring</span>
              </div>
              <div className="flex items-center gap-3 p-3 border rounded-lg">
                <div className="w-4 h-4 bg-yellow-500 rounded-full"></div>
                <span className="font-medium">40-59: Moderate Risk</span>
                <span className="text-sm text-gray-600 ml-auto">Schedule appointment</span>
              </div>
              <div className="flex items-center gap-3 p-3 border rounded-lg">
                <div className="w-4 h-4 bg-orange-500 rounded-full"></div>
                <span className="font-medium">60-79: Elevated Risk</span>
                <span className="text-sm text-gray-600 ml-auto">Seek prompt care</span>
              </div>
              <div className="flex items-center gap-3 p-3 border rounded-lg">
                <div className="w-4 h-4 bg-red-500 rounded-full"></div>
                <span className="font-medium">80-100: High Priority</span>
                <span className="text-sm text-gray-600 ml-auto">Urgent medical attention</span>
              </div>
            </div>
          </div>

          {/* Confidence & Reasoning */}
          {confidence && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Assessment Confidence</h3>
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-3 h-3 rounded-full ${
                    confidence >= 80 ? 'bg-green-500' : 
                    confidence >= 60 ? 'bg-yellow-500' : 'bg-orange-500'
                  }`}></div>
                  <span className="font-medium">{confidence}% Confidence</span>
                </div>
                <p className="text-sm text-gray-600">
                  Based on available medical evidence and symptom correlation strength
                </p>
              </div>
            </div>
          )}

          {/* Clinical Reasoning */}
          {reasoning && reasoning.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Clinical Reasoning</h3>
              <div className="space-y-2">
                {reasoning.map((reason, index) => (
                  <div key={index} className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-sm text-gray-700">{reason}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Disclaimer */}
          <Card className="p-4 bg-yellow-50 border-yellow-200">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-yellow-800 mb-1">Important Disclaimer</p>
                <p className="text-yellow-700">
                  This AI assessment is for informational purposes only and does not replace 
                  professional medical advice, diagnosis, or treatment. Always consult with 
                  qualified healthcare providers for medical decisions.
                </p>
              </div>
            </div>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AmIOKScoringExplanation;